import { observable, computed, makeObservable } from 'mobx'
import _ from 'lodash'
import { selectionTypes, defaultPolygonColor } from 'constants.js'
import circle from '@turf/circle'
import { point } from '@turf/helpers'
import api from 'api.js'
import Position from './common/Position'
import { catchError } from './ErrorStore'
import { selectionStore, dataStore } from './UIStore'
import { shapeTypes } from '../constants'
import { getDefaultGrayColor } from '../utils'
import i18next from '../i18n'

require('script-loader!wicket/wicket')

// Represents a buffer around a point, while the user is editing it.
// (After adding to a target group, the result will be a Geopolygon)
class PointBuffer {
  @observable name = ''
  @observable buffer = 10
  @observable bufferUnit = 'km'
  @observable initial = true
  @observable invalid = observable.map()
  @observable position = new Position()
  @observable color_id = null
  @observable transparency = 50
  @observable additional_info = []

  @computed get region () {
    const geo = this.position.geoJSON
    const numBuffer = Number(this.buffer)
    const isValidCoord = Array.isArray(geo) &&
      geo.length === 2 &&
      typeof geo[0] === 'number' && !isNaN(geo[0]) &&
      typeof geo[1] === 'number' && !isNaN(geo[1])
    const isValidRadius = !isNaN(numBuffer) && numBuffer > 0

    if (!isValidCoord || !isValidRadius) {
      return null
    }

    try {
      return circle(
        point(geo),
        numBuffer,
        {
          units: this.bufferUnit === 'mile' ? 'miles' : 'kilometers',
          steps: 64
        }
      ).geometry
    } catch (e) {
      return null
    }
  }

  @computed get getColor () {
    const item = dataStore.markerColors.find(item => item.id === this.color_id)

    return item ? item.active_color : defaultPolygonColor
  }

  constructor (isUpdating, buffer, position, name, id, targetGroupId, bufferUnit) {
    makeObservable(this)
    if (isUpdating) {
      this.isUpdating = true
      this.buffer = buffer
      this.bufferUnit = bufferUnit === 'mile' ? 'mile' : 'km'
      this.position.latLng = position
      this.name = name
      this.initial = false
      this.id = id
      this.targetGroupId = targetGroupId
    }
  }

  // Until the user clicks, `initial` is true and
  // the point follows the cursor. After clicking, the user can
  // drag the point, but otherwise can move their mouse without
  // the point following the cursor
  mapMousemove (e) {
    if (this.initial) {
      this.position.latLng = e.latlng
    }
  }

  mapMouseup (e) {
    this.initial = false
  }

  get selection () {
    return {
      item: this,
      type: selectionTypes.POINT_BUFFER,
      data: { focusedInteraction: true }
    }
  }

  @catchError
  async updateShape () {
    const wkt = new window.Wkt.Wkt()
    wkt.read(JSON.stringify(this.region))
    const result = await api.patchGeopolygon(this.id, {
      name: this.name,
      the_geom: wkt.write(),
      buffer: {
        radius: this.buffer,
        unit: this.bufferUnit,
        point: this.position.geoJSON
      },
      color: this.color_id,
      color_transparency: this.transparency
    })
    return result.data
  }

  validate (targetGroup) {
    const group = targetGroup || (selectionStore.data && selectionStore.data.group) || (dataStore.targetGroups && dataStore.targetGroups[0])
    const geopolygons = group && group.geopolygons ? group.geopolygons : []
    if (!this.isUpdating && this.name && _.find(geopolygons, { name: this.name.toString() })) {
      this.invalid.set('name', i18next.t('GLOBAL.DUPLICATE_ELEMENT_NAME', { element: i18next.t('GLOBAL.GEOPOLYGON') }))
      return false
    }

    this.invalid.clear()
    if (this.name === null || _.isEmpty(this.name.toString())) {
      this.invalid.set('name', i18next.t('INVALID.REQUIRED'))
    }
    if (this.position.longitude === null || _.isEmpty(this.position.longitude.toString())) {
      this.invalid.set('longitude', i18next.t('INVALID.REQUIRED'))
    }
    if (this.position.latitude === null || _.isEmpty(this.position.latitude.toString())) {
      this.invalid.set('latitude', i18next.t('INVALID.REQUIRED'))
    }
    if (this.buffer === null || _.isEmpty(this.buffer.toString())) {
      this.invalid.set('buffer', i18next.t('INVALID.REQUIRED'))
    } else if (this.buffer < 1) {
      this.invalid.set('buffer', i18next.t('INVALID.AT_LEAST_1'))
    }

    return this.invalid.size === 0
  }

  @catchError async add (targetGroupId) {
    if (this.validate()) {
      const wkt = new window.Wkt.Wkt()
      wkt.read(JSON.stringify(this.region))
      const effectiveColorId = this.color_id != null ? this.color_id : (getDefaultGrayColor(dataStore && dataStore.markerColors)?.id ?? null)
      const result = await api.postGeopolygon({
        name: this.name,
        the_geom: wkt.write(),
        target_group: targetGroupId,
        type: shapeTypes.POINT_BUFFER_SHAPE,
        color: effectiveColorId,
        color_transparency: this.transparency,
        buffer: {
          radius: this.buffer,
          unit: this.bufferUnit,
          point: this.position.geoJSON
        }
      })
      return result.data
    } else {
      return null
    }
  }

  /** Add point buffer to draft mode instead of saving to backend immediately */
  async addToDraft (poi) {
    if (!this.validate()) {
      return false
    }

    if (!this.region) {
      this.invalid.set('name', i18next.t('ITEM.PLACE_POINT_REQUIRED') || 'Please place a point first')
      return false
    }

    const effectiveColorId = this.color_id != null ? this.color_id : (getDefaultGrayColor(dataStore && dataStore.markerColors)?.id ?? null)

    // Store the polygon data in the POI draft state (this will trigger POI search)
    await poi.setDraftPolygon({
      name: this.name,
      geometry: this.region,
      color_id: effectiveColorId,
      transparency: this.transparency,
      type: 'pointBuffer',
      buffer: this.buffer,
      point: this.position.geoJSON
    })

    return true
  }
}
export default PointBuffer
